import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import type { Control, FieldValues, UseFormReturn } from 'react-hook-form';

import type { GetValueFormItems } from 'types';

interface SourceStore {
  types: Map<string, string>;
  listeners: Set<(ids: string | readonly string[]) => void>;
}

export const stores = new WeakMap<Control<FieldValues>, SourceStore>();

const getStore = (control: Control<FieldValues>) => {
  let store = stores.get(control);
  if (!store) {
    store = { types: new Map(), listeners: new Set() };
    stores.set(control, store);
  }
  return store;
};

export const registerPortalSource = (control: Control<FieldValues>, id: string, type: string) => {
  const store = getStore(control);
  if (store.types.get(id) === type) return;

  store.types.set(id, type);
  store.listeners.forEach((listener) => listener(id));
};

interface UsePortalFormValuesProps {
  enabled: boolean;
  form: UseFormReturn<FieldValues> | null;
  items: GetValueFormItems[];
}

export const usePortalFormValues = ({ enabled, form, items }: UsePortalFormValuesProps) => {
  const [resolved, setResolved] = useState<GetValueFormItems[] | null>(null);
  const control = form?.control;
  const getValues = form?.getValues;
  const watch = form?.watch;

  useEffect(() => {
    if (!enabled || !control || !getValues || !watch) return;

    const store = getStore(control);
    const sourceIds = new Set(items.map((item) => item.valueFormItemId));

    const refresh = (ids?: string | readonly string[]) => {
      const changedIds = ids === undefined ? undefined : new Set(typeof ids === 'string' ? [ids] : ids);
      if (changedIds && !Array.from(changedIds).some((id) => sourceIds.has(id))) return;
      setResolved((previous) => {
        const next = items.map((item, index) => {
          const cached = previous?.[index];
          const sourceId = item.valueFormItemId;
          if (cached && changedIds && (sourceId === null || !changedIds.has(sourceId))) return cached;

          let resolvedItem = item;
          if (sourceId !== null) {
            const value: unknown = getValues(sourceId);
            const values = value == null || value === '' ? [] : Array.isArray(value) ? value : [value];
            const type = store.types.get(sourceId);

            resolvedItem =
              type === 'Records' || type === 'RecordsExternal'
                ? {
                    ...item,
                    valueDirect: null,
                    valueRecordIds: values.map((entry) => String(typeof entry === 'object' && entry !== null ? entry.id : entry)),
                  }
                : {
                    ...item,
                    valueDirect: values.map(String),
                    valueFormItemId: null,
                    valueSourceType: null,
                  };
          }
          return cached && isEqual(resolvedItem, cached) ? cached : resolvedItem;
        });
        return previous && next.length === previous.length && next.every((item, index) => item === previous[index]) ? previous : next;
      });
    };

    refresh();

    store.listeners.add(refresh);

    const subscription = watch((_, { name }) => {
      if (!name) refresh();
    });

    return () => {
      store.listeners.delete(refresh);
      subscription.unsubscribe();
    };
  }, [enabled, control, getValues, watch, items]);

  return resolved;
};
